/**
 * Storage guard per la tab impersonate.
 *
 * Tre meccanismi di isolamento applicati SOLO quando siamo in modalità impersonate
 * (flag `flusia_impersonate_mode === '1'` in sessionStorage):
 *
 * 1. **Monkey-patch localStorage.getItem/setItem/removeItem**: per chiavi user-data
 *    (whitelist `BLACKLIST_KEYWORDS`), redirige le operazioni a sessionStorage prefissato
 *    `__imp__<key>`. Le altre chiavi (theme, sidebar, scroll position) continuano sul
 *    localStorage reale (cosmetico, condivisibile).
 *
 * 2. **Intercept storage events cross-tab**: il browser emette `window.onstorage` ad
 *    altre tab quando una tab scrive su localStorage. Questo permetterebbe a un evento
 *    della tab admin di propagare al listener nella tab impersonate (es. `useLocalStorage`
 *    hook che si aggiorna). Wrappiamo `addEventListener('storage', ...)` per filtrare
 *    via gli eventi delle chiavi blacklist.
 *
 * 3. **Niente effetti sulla tab admin**: il guard si auto-disabilita se `flusia_impersonate_mode`
 *    non è settato. La tab admin continua normalmente.
 *
 * Importante: deve essere chiamata in main.tsx PRIMA di qualunque import che istanzia
 * store/hook che leggono localStorage. In pratica l'installazione canonica avviene via
 * `<script>` inline in `index.html` (gli script ESM sono `defer` per spec) — questa
 * funzione resta come safety net idempotente in dev/HMR.
 *
 * --- DUE FLAG, DUE STATI DIVERSI ---
 *
 *   `flusia_impersonate_mode = '1'` → "questa tab è una tab impersonate".
 *      Settato dal boot inline in `index.html` (path `/impersonate` o flag già presente).
 *      Persiste in sessionStorage per tutta la vita della tab. Usato da:
 *      - `isImpersonateMode()` (qui) per attivare il guard
 *      - `activeRoleStore` per scegliere sessionStorage vs localStorage
 *
 *   `impersonate_active = '1'` → "sessione del target stabilita con successo".
 *      Settato da `ImpersonateLanding.tsx` DOPO `verifyOtp()` riuscito.
 *      Usato da:
 *      - `ImpersonateBar` per mostrarsi solo dopo il login completato (non durante loading)
 *      - `supabase.ts` come fallback per `isImpersonateContext` dopo redirect a `/dashboard`
 *
 *   I due flag NON sono ridondanti: rappresentano stati distinti del lifecycle.
 */

const BLACKLIST_KEYWORDS = [
  // WhatsApp e chat (drafts, config attiva, undo, tooltip, ecc.)
  'whatsapp',
  'chat',
  'message',
  // Cronologia e ultime visualizzazioni
  'recent-searches',
  'editClientId',
  // Ruolo attivo (gestito da activeRoleStore tab-aware ma blacklist per safety)
  'flusia_active_role',
  // Preferenze UI utente-specifiche (sidebar, header filtri, vista task)
  'filters-pinned',
  'clientSidePanelCollapsed',
  'collapsed-task-sections',
  'collapsed-document-sections',
  'task-view',
  // Welcome / onboarding flags (per non riproporre wizard al target appena loggato)
  'welcome-modal',
  'onboarding',
  // Zustand persist middleware keys (default storage = localStorage)
  'settings-storage',
  'client-portal-auth',
  'collaborator-portal-auth',
  'partner-portal-auth',
  // Direct localStorage usage in stores/hooks (audit 2026-05-06)
  'google_calendar',           // calendarStore: list cache + sync token
  'selectedleadpipelineid',    // leadPipelineStore: pipeline selection per org
  'gmail_drawer',              // gmailDrawerStore: drawer mode
  'ticket_active_timer',       // ticketTimeEntryStore: active timer
  '2fa_verification_pending',  // useTwoFactor: pending 2FA state (auth-sensitive)
  'wiki-last-page',            // wikiStore: last viewed page
  'query_cache',               // useQuery hook: generic data cache (query_cache_*)
  '_filters',                  // page filters: clients_filters, leads_filters, deals_filters, meetings_page_filters
];

const PREFIX = '__imp__';

function isUserDataKey(key: string): boolean {
  const lower = key.toLowerCase();
  return BLACKLIST_KEYWORDS.some(k => lower.includes(k.toLowerCase()));
}

function isImpersonateMode(): boolean {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem('flusia_impersonate_mode') === '1';
}

export function installImpersonateStorageGuard(): void {
  if (!isImpersonateMode()) return;
  if ((window as any).__impersonateGuardActive) {
    console.log('[ImpersonateStorageGuard] already active (inline bootstrap), skipping module install');
    return;
  }

  const ls = window.localStorage;
  const ss = window.sessionStorage;

  // 1. Patch dei metodi di localStorage
  const origGetItem = ls.getItem.bind(ls);
  const origSetItem = ls.setItem.bind(ls);
  const origRemoveItem = ls.removeItem.bind(ls);

  ls.getItem = function (key: string): string | null {
    if (isUserDataKey(key)) {
      return ss.getItem(`${PREFIX}${key}`);
    }
    return origGetItem(key);
  };

  ls.setItem = function (key: string, value: string): void {
    if (isUserDataKey(key)) {
      ss.setItem(`${PREFIX}${key}`, value);
      return;
    }
    origSetItem(key, value);
  };

  ls.removeItem = function (key: string): void {
    if (isUserDataKey(key)) {
      ss.removeItem(`${PREFIX}${key}`);
      return;
    }
    origRemoveItem(key);
  };

  // 2. Intercept storage events cross-tab (via window.onstorage)
  // Il browser emette eventi `storage` alle ALTRE tab quando una scrive su localStorage.
  // Senza filtro, useLocalStorage hook nella tab impersonate riceverebbe l'evento dalla
  // tab admin e si aggiornerebbe con il valore admin → leak.
  const origAddEventListener = window.addEventListener.bind(window);
  // @ts-expect-error: override generico signature
  window.addEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ) {
    if (type === 'storage' && typeof listener === 'function') {
      const wrapped = function (event: Event) {
        const e = event as StorageEvent;
        if (e.key && isUserDataKey(e.key)) {
          // Eventi su chiavi blacklist: silenziati per la tab impersonate
          return;
        }
        (listener as (ev: Event) => void)(event);
      };
      // Salviamo una mappa listener → wrapped per gestire removeEventListener (best effort)
      // Per semplicità lasciamo che removeEventListener funzioni solo se l'utente passa la
      // stessa funzione (che è una limitazione raramente problematica).
      return origAddEventListener(type, wrapped as EventListener, options);
    }
    return origAddEventListener(type, listener, options);
  };

  (window as any).__impersonateGuardActive = true;
  console.log('[ImpersonateStorageGuard] active — user-data keys redirected, storage events filtered');
}
